import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const propertyTypes = [
  "Multifamily",
  "Mixed-Use",
  "Retail",
  "Industrial",
  "Land",
  "Subdivisions",
  "Build-to-Rent",
  "Commercial Development",
  "Other",
];

const projectStages = [
  "Concept / Pre-Development",
  "Entitlements / Approvals",
  "Shovel-Ready",
  "Under Construction",
  "Existing / Stabilized Asset",
];

const closingTimelines = ["30 Days", "60 Days", "90 Days", "3–6 Months", "Flexible"];

const emptyForm = {
  name: "",
  company: "",
  phone: "",
  email: "",
  property_location: "",
  property_type: "",
  loan_amount: "",
  total_project_cost: "",
  current_value: "",
  equity_available: "",
  project_stage: "",
  closing_timeline: "",
  additional_info: "",
};

const inputCls =
  "w-full bg-[#111111] border border-white/10 focus:border-[#D4AF37]/60 px-4 py-3.5 text-sm font-light tracking-wide text-[#F5F5F5] placeholder:text-[#A3A3A3]/60 outline-none transition-colors duration-300";

const labelCls = "block text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2.5";

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp,.zip";

const FileBox = ({ id, label, hint, file, onChange, testid }) => (
  <div>
    <span className={labelCls}>{label}</span>
    <div className="flex items-center gap-3">
      <label
        htmlFor={id}
        data-testid={testid}
        className="flex flex-1 items-center justify-between gap-4 cursor-pointer border border-dashed border-[#D4AF37]/30 bg-[#111111] px-4 py-4 hover:border-[#D4AF37]/70 transition-colors duration-300"
      >
        <span className={`text-sm font-light tracking-wide truncate ${file ? "text-[#F5F5F5]" : "text-[#A3A3A3]/60"}`}>
          {file ? file.name : hint}
        </span>
        <Upload size={15} strokeWidth={1.5} className="text-[#D4AF37] shrink-0" />
      </label>
      {file && (
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid={`${testid}-clear`}
          aria-label={`Remove ${label}`}
          className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors p-1"
        >
          <X size={16} />
        </button>
      )}
    </div>
    <input
      id={id}
      type="file"
      accept={ACCEPT}
      className="hidden"
      onChange={(e) => {
        onChange(e.target.files?.[0] || null);
        e.target.value = "";
      }}
    />
  </div>
);

export default function FundingForm() {
  const [form, setForm] = useState(emptyForm);
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState(null);
  const [phase, setPhase] = useState("");

  const sending = phase !== "";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API}/funding/upload`, fd);
    return res.data;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    try {
      const documents = [];
      if (summary) {
        setPhase("Uploading executive summary...");
        documents.push(await upload(summary));
      }
      if (plans) {
        setPhase("Uploading plans...");
        documents.push(await upload(plans));
      }
      setPhase("Submitting for review...");
      await axios.post(`${API}/funding/applications`, { ...form, documents });
      toast.success("Project received — our finance team will review it and call you shortly.");
      setForm(emptyForm);
      setSummary(null);
      setPlans(null);
    } catch (err) {
      toast.error("Something went wrong. Please call 816-405-6181 or email funding@veritasdevelopmentgroupllc.com.");
    } finally {
      setPhase("");
    }
  };

  return (
    <section id="funding-form" className="relative py-24 lg:py-32 border-t border-[#D4AF37]/10 bg-[#080808]" data-testid="funding-form-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-center mb-16"
        >
          <p className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-6" data-testid="funding-form-overline">
            Request Financing
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.08] tracking-tight text-[#F5F5F5]" data-testid="funding-form-headline">
            Submit Your <span className="italic text-[#D4AF37]">Project</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-base font-light leading-relaxed tracking-wide text-[#A3A3A3]">
            Share the essentials of your opportunity and our finance team will review it for capital placement.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.1 }}
          onSubmit={submit}
          className="relative border border-[#D4AF37]/20 bg-[#0A0A0A] p-8 sm:p-12"
          data-testid="funding-form"
        >
          <span className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#D4AF37] -translate-x-px -translate-y-px" aria-hidden="true" />
          <span className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-[#D4AF37] translate-x-px translate-y-px" aria-hidden="true" />
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="funding-name" className={labelCls}>Name *</label>
                <input id="funding-name" data-testid="funding-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="funding-company" className={labelCls}>Company</label>
                <input id="funding-company" data-testid="funding-company-input" value={form.company} onChange={set("company")} placeholder="Company or entity name" className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="funding-phone" className={labelCls}>Phone *</label>
                <input id="funding-phone" data-testid="funding-phone-input" required value={form.phone} onChange={set("phone")} placeholder="(816) 000-0000" className={inputCls} />
              </div>
              <div>
                <label htmlFor="funding-email" className={labelCls}>Email *</label>
                <input id="funding-email" data-testid="funding-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="funding-location" className={labelCls}>Property Location *</label>
                <input id="funding-location" data-testid="funding-location-input" required value={form.property_location} onChange={set("property_location")} placeholder="City, state or address" className={inputCls} />
              </div>
              <div>
                <label htmlFor="funding-type" className={labelCls}>Property Type</label>
                <select id="funding-type" data-testid="funding-type-select" value={form.property_type} onChange={set("property_type")} className={`${inputCls} appearance-none`}>
                  <option value="" disabled>Select a property type</option>
                  {propertyTypes.map((t) => (
                    <option key={t} value={t} className="bg-[#111111]">{t}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="funding-loan" className={labelCls}>Loan Amount Requested *</label>
                <input id="funding-loan" data-testid="funding-loan-input" required value={form.loan_amount} onChange={set("loan_amount")} placeholder="$2,500,000" className={inputCls} />
              </div>
              <div>
                <label htmlFor="funding-cost" className={labelCls}>Total Project Cost</label>
                <input id="funding-cost" data-testid="funding-cost-input" value={form.total_project_cost} onChange={set("total_project_cost")} placeholder="$4,000,000" className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="funding-value" className={labelCls}>Current Value / Purchase Price</label>
                <input id="funding-value" data-testid="funding-value-input" value={form.current_value} onChange={set("current_value")} placeholder="$3,200,000" className={inputCls} />
              </div>
              <div>
                <label htmlFor="funding-equity" className={labelCls}>Equity Available</label>
                <input id="funding-equity" data-testid="funding-equity-input" value={form.equity_available} onChange={set("equity_available")} placeholder="$800,000" className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="funding-stage" className={labelCls}>Project Stage</label>
                <select id="funding-stage" data-testid="funding-stage-select" value={form.project_stage} onChange={set("project_stage")} className={`${inputCls} appearance-none`}>
                  <option value="" disabled>Select project stage</option>
                  {projectStages.map((s) => (
                    <option key={s} value={s} className="bg-[#111111]">{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="funding-timeline" className={labelCls}>Closing Timeline</label>
                <select id="funding-timeline" data-testid="funding-timeline-select" value={form.closing_timeline} onChange={set("closing_timeline")} className={`${inputCls} appearance-none`}>
                  <option value="" disabled>Select a timeline</option>
                  {closingTimelines.map((t) => (
                    <option key={t} value={t} className="bg-[#111111]">{t}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <FileBox id="funding-summary-file" label="Upload Executive Summary / Pro Forma" hint="PDF, DOC, XLS — max 25 MB" file={summary} onChange={setSummary} testid="funding-summary-upload" />
              <FileBox id="funding-plans-file" label="Upload Plans" hint="PDF, images, ZIP — max 25 MB" file={plans} onChange={setPlans} testid="funding-plans-upload" />
            </div>
            <div>
              <label htmlFor="funding-info" className={labelCls}>Additional Information</label>
              <textarea id="funding-info" data-testid="funding-info-input" rows={4} value={form.additional_info} onChange={set("additional_info")} placeholder="Tell us about the opportunity, sponsorship experience, exit strategy..." className={`${inputCls} resize-none`} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="funding-submit-button"
              className="w-full bg-[#D4AF37] text-black py-4 text-[11px] tracking-[0.3em] uppercase font-medium hover:bg-[#F3E5AB] disabled:opacity-60 transition-colors duration-300"
            >
              {sending ? phase : "Submit Project for Review"}
            </button>
            <p className="text-center text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]/70">
              Prefer to talk? Call for funding today — <a href="tel:+18164056181" data-testid="funding-form-phone-link" className="text-[#D4AF37]">816-405-6181</a>
              {" "}or <a href="mailto:funding@veritasdevelopmentgroupllc.com" data-testid="funding-form-email-link" className="text-[#D4AF37]">email our finance team</a>
            </p>
            <p className="text-center text-[9px] font-light leading-relaxed tracking-[0.12em] uppercase text-[#A3A3A3]/50" data-testid="funding-form-disclosure">
              Veritas Development Group LLC acts in a broker and advisory capacity, arranging capital through third-party lending and capital-market relationships. Financing is provided by third-party lenders and capital partners, subject to underwriting, eligibility and lender approval. Veritas is not a direct lending financial institution.
            </p>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
